/**
 * Kiosk / andon mode — press-side monitoring for an overhead screen.
 *
 * Deliberately NOT the station page: no navigation, no admin controls,
 * extremely low cognitive load, readable from distance. The advisory strip is
 * permanent because the limitation travels with the verdict.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useApi, useEventStream } from '../hooks/useApi';
import type { CaptureResult, Station } from '../api/types';
import { StatePill } from '../components/Metric';

export function KioskPage() {
  const navigate = useNavigate();
  const { data: stations } = useApi<Station[]>(() => api.get<Station[]>('/api/v1/stations'), []);
  const [idx, setIdx] = useState(0);
  const station = useMemo(() => stations?.[idx] ?? stations?.[0] ?? null, [stations, idx]);

  const { messages, connected } = useEventStream(10);
  const last = useMemo(
    () => messages.find((m) => m.type === 'inspection_event')?.payload as CaptureResult | undefined,
    [messages],
  );
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const outcome = last?.outcome;
  const verdict =
    outcome === 'PASS'
      ? 'PASS'
      : outcome === 'INSUFFICIENT_DATA'
        ? 'NEEDS REVIEW'
        : outcome === 'REJECT_NON_SAFETY_GATE'
          ? 'REJECT'
          : outcome
            ? 'CHECK PART'
            : null;
  const tone = outcome === 'PASS' ? 'pass' : outcome && outcome !== 'INSUFFICIENT_DATA' ? 'flag' : 'idle';

  return (
    <div className="kiosk">
      <div className="kiosk-top">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="#0a5fae" strokeWidth="2" />
          <circle cx="12" cy="12" r="3.5" fill="#0a5fae" />
        </svg>
        <div>
          <div className="name">{station ? `${station.machine?.site?.name ?? ''} · ${station.machine?.name ?? ''} · ${station.name}` : 'PlasticVision'}</div>
          <div className="ctx">
            {station?.activeRecipe?.name ?? 'No recipe bound'}
            {last ? ` · last update ${now.toLocaleTimeString('en-GB', { hour12: false })}` : ''}
          </div>
        </div>
        {station && <StatePill state={station.state} />}
        <span className={`pill ${connected ? 'ok' : 'warn'}`}>{connected ? 'Connected' : 'Reconnecting'}</span>
        <div style={{ flex: 1 }} />
        {stations && stations.length > 1 && (
          <select
            aria-label="Station"
            style={{ maxWidth: 260 }}
            value={idx}
            onChange={(e) => setIdx(Number(e.target.value))}
          >
            {stations.map((s, i) => (
              <option key={s.id} value={i}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        <button className="btn sm ghost" onClick={() => navigate('/station')}>
          Exit kiosk
        </button>
      </div>

      <div className="kiosk-main">
        <div className="kiosk-visual">
          {last?.mediaKeys?.[0] ? (
            <img src={`/api/v1/media/${encodeURIComponent(last.mediaKeys[0]!)}`} alt="Latest inspection frame" />
          ) : (
            'Waiting for the first inspection frame…'
          )}
        </div>

        <div className={`kiosk-verdict ${tone}`}>
          <div className="big">{verdict ?? '—'}</div>
          {last && (
            <>
              <div style={{ fontSize: 16, color: 'var(--ink-2)' }}>
                {last.defectName ?? (last.sufficientEvidence ? 'Within baseline' : 'Not enough evidence')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                {last.sufficientEvidence ? `confidence ${(last.confidence * 100).toFixed(0)}% · ` : ''}
                {last.latencyMs} ms
              </div>
              <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                action: {last.actionTaken}
              </div>
              {last.adapterKind === 'SIMULATION' && (
                <span className="pill warn">SIMULATION — not an AI model</span>
              )}
            </>
          )}
        </div>
      </div>

      <div className="kiosk-strip">
        <span className="tag advisory">Advisory</span>
        This display advises only. It never stops the machine and is not a safety system.
      </div>
    </div>
  );
}
